import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/card';
import { RAGBadge, RAGStatus } from '../machines/RAGBadge';
import { productDetailPath } from '@/lib/entityPaths';
import { entityLinkClassName } from '@/lib/entityLink';
import { cn } from '../../lib/utils';
import { Package } from 'lucide-react';

interface SlotCardProps {
  slotNumber: number;
  slotLabel: string | null;
  productId?: string | null;
  productName?: string;
  /** Resolved thumbnail URL (catalog imageUrl or first IMAGE media). */
  productImageUrl?: string | null;
  currentQuantity: number;
  maxCapacity: number;
  ragStatus: RAGStatus;
  /** Replaces the quantity line when provided (e.g. planogram price + margin). */
  footer?: ReactNode;
  onClick?: () => void;
  className?: string;
}

export function SlotCard({
  slotNumber,
  slotLabel,
  productId,
  productName,
  productImageUrl,
  currentQuantity,
  maxCapacity,
  ragStatus,
  footer,
  onClick,
  className,
}: SlotCardProps) {
  const borderColor = {
    GREEN: 'border-rag-good',
    AMBER: 'border-rag-warning',
    RED: 'border-rag-critical',
  }[ragStatus];

  const label = slotLabel || `#${slotNumber}`;
  const name = productName?.trim() || 'No product';
  const productHref = productId?.trim() ? productDetailPath(productId.trim()) : undefined;

  return (
    <Card
      className={cn(
        'h-full min-h-0 w-full max-w-full border-2 p-4 transition-shadow duration-200 ease-out flex flex-col',
        onClick && 'cursor-pointer hover:shadow-md',
        borderColor,
        className
      )}
      onClick={onClick}
    >
      <div className="flex min-h-0 flex-1 items-center gap-3">
        <div className="h-24 w-24 shrink-0 overflow-hidden rounded-md">
          {productImageUrl ? (
            <img
              src={productImageUrl}
              alt=""
              className="h-full w-full object-contain object-center"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground">
              <Package className="size-8 opacity-60" aria-hidden />
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1 flex flex-col gap-2">
          <div className="flex items-start justify-between gap-2">
            <span className="font-semibold text-sm tabular-nums shrink-0">{label}</span>
            <RAGBadge variant="dot" status={ragStatus} size="sm" className="shrink-0 mt-0.5" />
          </div>
          {productHref && !onClick ? (
            <Link
              to={productHref}
              className={cn(
                'text-xs text-muted-foreground leading-snug break-words [overflow-wrap:anywhere]',
                entityLinkClassName
              )}
              title={name}
              onClick={(e) => e.stopPropagation()}
            >
              {name}
            </Link>
          ) : (
            <p
              className="text-xs text-muted-foreground leading-snug break-words [overflow-wrap:anywhere]"
              title={name === 'No product' ? undefined : name}
            >
              {name}
            </p>
          )}
          <div className="pt-1 text-xs font-medium text-foreground tabular-nums">
            {footer ?? `${currentQuantity}/${maxCapacity}`}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default SlotCard;
